import React, { useEffect, useState } from 'react';
import { useQuery } from 'hooks/apollo';
import { useMediaQuery } from 'react-responsive';
import classnames from 'shared/utils/classnames';
import appTheme from 'shared/constants/theme.json';
import globalMessages from 'translations/messages';
import { THEMES } from 'shared/constants/design';

import { FormattedMessage } from 'react-intl';
import { Button, Form, Layout } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';

import Loading from 'components/Loading';
import Page from 'components/Page';
import Wizard from 'components/StepsWizard';
import Preview from 'components/SurveyPreview';
import LanguageTag from 'components/LanguageTag';

import Publish from './PublishDistribution';
import EditDesign from './EditDistributionDesign';
import EditStyle from './EditDistributionStyle';
import EditAudience from './EditDistributionAudience';

import messages from './messages';
import query from './query.gql';
import './edit-distribution.less';

const STEPS = {
  STYLE: 'style',
  DESIGN: 'design',
  AUDIENCE: 'audience',
  PUBLISH: 'publish',
};

const EditDistribution = ({ distributionId, step, goTo }) => {
  const { loading, data: { distribution } = {} } = useQuery(query, {
    variables: { id: distributionId },
  });

  const isSmallScreen = useMediaQuery({ query: `(max-width: ${appTheme.laptopBreakpoint})` });
  const [siderCollapsed, setSiderCollapsed] = useState(isSmallScreen);
  const togglePreview = () => setSiderCollapsed(!siderCollapsed);

  const [design, setDesign] = useState({});

  const [publishForm] = Form.useForm();
  const [designForm] = Form.useForm();
  const [styleForm] = Form.useForm();
  const [audienceForm] = Form.useForm();

  const onDesignChange = () => {
    const theme = designForm.getFieldValue('theme') || distribution?.theme || 'default';

    setDesign({
      ...THEMES[theme],
      ...(theme === 'custom' ? distribution?.customTheme : {}),
      ...(theme === 'custom' ? designForm.getFieldValue('customTheme') : {}),
    });
  };

  useEffect(() => {
    if (distribution) {
      onDesignChange();
    }
  }, [distribution]);

  if (loading) { return <Loading />; }

  const { survey, customTheme } = distribution;
  const { customer } = survey;
  const logo = {
    source: distribution.logoSource,
    height: distribution.logoHeight,
    url: distribution.logoUrl,
    customerLogoUrl: customer.logo?.url,
  };
  const stepKeys = Object.values(STEPS);

  const onFinished = () => goTo({ surveyId: survey.id, step: 'distributions', distributionId: null, distributionStep: null });

  const steps = [
    {
      key: STEPS.STYLE,
      title: <FormattedMessage {...messages.style} />,
      render: () => (
        <EditStyle customerId={customer.id} distribution={distribution} form={styleForm} />
      ),
    },
    {
      key: STEPS.DESIGN,
      title: <FormattedMessage {...messages.design} />,
      render: () => (
        <EditDesign
          id={distributionId}
          logo={logo}
          theme={distribution.theme}
          customTheme={customTheme}
          useToggleButtons={distribution.useToggleButtons}
          form={designForm}
          onChange={onDesignChange}
          activeLicense={distribution.website?.dataSource?.activeLicense}
        />
      ),
    },
    {
      key: STEPS.AUDIENCE,
      title: <FormattedMessage {...messages.audience} />,
      render: () => (
        <EditAudience distribution={distribution} form={audienceForm} />
      ),
    },
    {
      key: STEPS.PUBLISH,
      title: <FormattedMessage {...messages.publish} />,
      render: () => (
        <Publish customerId={customer.id} distribution={distribution} form={publishForm} onDone={onFinished} />
      ),
    },
  ];

  const onStepChange = (index) => {
    const activeKey = stepKeys[index];
    goTo({ distributionStep: activeKey });
  };

  const submitStep = async (index) => {
    let form = null;
    if (index === stepKeys.indexOf(STEPS.STYLE)) {
      form = styleForm;
    } else if (index === stepKeys.indexOf(STEPS.DESIGN)) {
      form = designForm;
    } else if (index === stepKeys.indexOf(STEPS.AUDIENCE)) {
      form = audienceForm;
    } else if (index === stepKeys.indexOf(STEPS.PUBLISH)) {
      form = publishForm;
    }

    if (form) {
      await form.validateFields();
      form.submit();
    }
  };

  const getInitialStep = () => {
    const key = stepKeys.findIndex(k => k === step);
    return key !== -1 ? key : 0;
  };

  return (
    <Layout className="edit-distribution">
      <Layout.Content className="edit-distribution__content">
        <Page>
          <Page.Header
            title={<FormattedMessage {...globalMessages.distribution} />}
            extra={(
              <>
                <LanguageTag {...survey} />
                {survey.title}
              </>
            )}
          />
          <Page.Content>
            <Wizard
              steps={steps}
              onChange={onStepChange}
              onNext={submitStep}
              initalKey={getInitialStep()}
            />
          </Page.Content>
        </Page>
      </Layout.Content>
      <Layout.Sider
        theme="light"
        className={classnames(
          'edit-distribution__preview',
          siderCollapsed && 'edit-distribution__preview--collapsed',
        )}
        collapsible
        trigger={null}
        collapsed={siderCollapsed}
      >
        <div className="edit-distribution__preview-header">
          <Button
            type="link"
            onClick={togglePreview}
            icon={siderCollapsed ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
          />
          {!siderCollapsed && <FormattedMessage {...messages.preview} />}
        </div>
        {!siderCollapsed && (
          <Preview
            distributionId={distributionId}
            logoSource={designForm.getFieldValue('logoSource') || logo.source}
            logoUrl={designForm.getFieldValue('logoUrl') || logo.url}
            logoHeight={designForm.getFieldValue('logoHeight') || logo.height}
            useToggleButtons={designForm.getFieldValue('useToggleButtons')}
            theme={designForm.getFieldValue('theme')}
            customTheme={design}
          />
        )}
      </Layout.Sider>
    </Layout>
  );
};

export default EditDistribution;
